<template>
  <section class="page-template login-page">
    <div class="page-template__panel login-page__panel">
      <span class="label">Console Access</span>
      <h1 class="page-template__title login-page__title">Sign in</h1>
      <p class="page-template__copy login-page__subtitle">Use your console credentials to access the operator surface.</p>

      <div v-if="errorMessage" class="alert alert--error login-page__error">
        {{ errorMessage }}
      </div>

      <form class="form-stack login-page__form" @submit.prevent="handleLogin">
        <div class="form-field">
          <label for="username" class="form-label">Username</label>
          <input
            id="username"
            name="username"
            ref="usernameInput"
            v-model="username"
            type="text"
            class="form-input"
            autocomplete="username"
            :disabled="loading"
          />
        </div>

        <div class="form-field">
          <label for="password" class="form-label">Password</label>
          <input
            id="password"
            name="password"
            ref="passwordInput"
            v-model="password"
            type="password"
            class="form-input"
            autocomplete="current-password"
            :disabled="loading"
          />
        </div>

        <button
          type="submit"
          class="button button--primary"
          :disabled="loading"
        >
          {{ loading ? 'Signing in...' : 'Sign In' }}
        </button>
      </form>

      <router-link to="/" class="link-muted login-page__back">Back to overview</router-link>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuth } from '@/composables/useAuth.js'
import { useOrchestratorBaseUrl } from '@/composables/useOrchestratorBaseUrl.js'
import { apiErrorMessage } from '@/services/apiErrorMessage.js'

const router = useRouter()
const route = useRoute()
const { setToken } = useAuth()
const { orchestratorBaseUrl, apiHeaders } = useOrchestratorBaseUrl()

const username = ref('')
const password = ref('')
const loading = ref(false)
const errorMessage = ref('')
const usernameInput = ref(null)
const passwordInput = ref(null)

async function handleLogin() {
  const submittedUsername = username.value || usernameInput.value?.value || ''
  const submittedPassword = password.value || passwordInput.value?.value || ''

  if (!submittedUsername.trim() || !submittedPassword) {
    errorMessage.value = 'Enter a username and password'
    return
  }

  loading.value = true
  errorMessage.value = ''

  try {
    const response = await fetch(`${orchestratorBaseUrl.value}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...apiHeaders.value,
      },
      body: JSON.stringify({ username: submittedUsername, password: submittedPassword }),
    })

    if (response.ok) {
      const data = await response.json()
      setToken(data.token)
      const raw = Array.isArray(route.query.redirect) ? route.query.redirect[0] : route.query.redirect || '/console'
      // Prevent open redirect: only allow relative paths starting with /
      const redirect = typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/console'
      router.push(redirect)
    } else if (response.status === 401) {
      errorMessage.value = 'Invalid credentials'
    } else {
      const data = await response.json().catch(() => ({}))
      errorMessage.value = apiErrorMessage(data, response.status)
    }
  } catch {
    errorMessage.value = 'Unable to connect to server'
  } finally {
    loading.value = false
  }
}
</script>
